import type {
  GroupComparisonsCohortDistribution,
  GroupComparisonsFilterParams,
  GroupComparisonsFilterResult,
  GroupComparisonsMetricFocus,
  GroupComparisonsSchoolRow,
  GroupComparisonsSummary,
} from '@/features/reports/api/groupComparisons.dto';
import {
  buildComposedPeerGroupLabel,
} from '@/features/reports/utils/bandDisplayLabels';

import {
  GROUP_COMPARISONS_OUTLIER_THRESHOLD_NAME,
} from '@/features/settings/dashboardSettingNames';
import { getDefaultThresholdFromConfig } from '@/features/settings/utils/resolveDashboardSetting';

export { buildComposedPeerGroupLabel };

export function defaultGroupComparisonsOutlierThreshold(): number {
  return getDefaultThresholdFromConfig(GROUP_COMPARISONS_OUTLIER_THRESHOLD_NAME);
}

function buildOutlierReasons(
  vCost: number | null,
  vTuition: number | null,
  vSalary: number | null,
  outlierThreshold: number
): string[] {
  const outlierReasons: string[] = [];
  if (vCost != null && Math.abs(vCost) >= outlierThreshold) {
    outlierReasons.push(`Cost ${vCost > 0 ? '+' : ''}${vCost}% vs peers`);
  }
  if (vTuition != null && Math.abs(vTuition) >= outlierThreshold) {
    outlierReasons.push(`Tuition ${vTuition > 0 ? '+' : ''}${vTuition}% vs peers`);
  }
  if (vSalary != null && Math.abs(vSalary) >= outlierThreshold) {
    outlierReasons.push(`Salary ${vSalary > 0 ? '+' : ''}${vSalary}% vs peers`);
  }
  return outlierReasons;
}

/** Mock governorate ids aligned with GET /api/app/region/governerates */
export const MOCK_GOVERNORATE_IDS = [8, 9, 10, 11, 12, 13, 14] as const;

export const MOCK_GOVERNORATE_NAMES: Record<number, string> = {
  8: 'جبل لبنان',
  9: 'النبطية',
  10: 'الجنوب',
  11: 'بيروت',
  12: 'البقاع',
  13: 'الشمال',
  14: 'بعلبك والهرمل',
};

/** Names aligned with GET /api/app/school-bands */
const MOCK_SCHOOL_BANDS = [
  { id: 'mock-sb-small', name: 'Small', minValue: 0, maxValue: 160 },
  { id: 'mock-sb-medium', name: 'Medium', minValue: 161, maxValue: 317 },
  { id: 'mock-sb-large', name: 'Large', minValue: 318, maxValue: 636 },
  { id: 'mock-sb-very-large', name: 'Very Large', minValue: 637, maxValue: 3605 },
] as const;

const MOCK_TUITION_TIERS = [
  { name: 'Low', minValue: 425, maxValue: 1695 },
  { name: 'Lower-middle', minValue: 1237, maxValue: 1702 },
  { name: 'Upper-middle', minValue: 1709, maxValue: 2223 },
  { name: 'High', minValue: 2225, maxValue: 9507 },
] as const;

/** Names aligned with GET /api/app/parent-tuition-band */
const MOCK_PARENT_BANDS = [
  { id: 'mock-pb-budget', name: 'Budget', minValue: 0, maxValue: 1466 },
  { id: 'mock-pb-standard', name: 'Standard', minValue: 1467, maxValue: 2006 },
  { id: 'mock-pb-mid', name: 'Mid-Premium', minValue: 2007, maxValue: 3047 },
  { id: 'mock-pb-premium', name: 'Premium', minValue: 3048, maxValue: 17723 },
] as const;

const SCHOOL_NAME_PARTS = [
  'Al-Noor', 'Al-Amal', 'Al-Hikma', 'Saint Joseph', 'Modern Learning', 'Future Leaders',
  'Beacon', 'Horizon', 'Excellence', 'Harmony', 'Pioneer', 'Radiant', 'Summit', 'Unity',
];

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function variancePct(school: number, peer: number): number | null {
  if (!Number.isFinite(school) || !Number.isFinite(peer) || peer === 0) return null;
  return round2(((school - peer) / peer) * 100);
}

function peerSpread(median: number, spread = 0.22): { min: number; max: number } {
  const m = Math.max(median, 1);
  return { min: round2(m * (1 - spread)), max: round2(m * (1 + spread)) };
}

export function hasAtLeastOneGroupFilter(params: GroupComparisonsFilterParams): boolean {
  return Boolean(
    (params.governorateId != null && Number.isFinite(params.governorateId)) ||
      params.schoolBandId?.trim() ||
      params.tuitionBandId?.trim() ||
      params.parentBandId?.trim()
  );
}

function matchesRow(
  row: GroupComparisonsSchoolRow,
  params: GroupComparisonsFilterParams,
  nameHints?: { schoolBandName?: string; tuitionBandName?: string; parentBandName?: string }
): boolean {
  if (params.governorateId != null && row.governorateId !== params.governorateId) return false;
  if (params.schoolBandId?.trim()) {
    const idOk = row.schoolBandId === params.schoolBandId.trim();
    const nameOk = Boolean(nameHints?.schoolBandName && row.schoolBandName === nameHints.schoolBandName);
    if (!idOk && !nameOk) return false;
  }
  if (params.tuitionBandId?.trim()) {
    const idOk = row.tuitionBandId === params.tuitionBandId.trim();
    const nameOk = Boolean(nameHints?.tuitionBandName && row.tuitionBandName === nameHints.tuitionBandName);
    if (!idOk && !nameOk) return false;
  }
  if (params.parentBandId?.trim()) {
    const idOk = row.parentBandId === params.parentBandId.trim();
    const nameOk = Boolean(nameHints?.parentBandName && row.parentBandName === nameHints.parentBandName);
    if (!idOk && !nameOk) return false;
  }
  return true;
}

function generateSchoolsForCohort(
  schoolBand: (typeof MOCK_SCHOOL_BANDS)[number],
  tuitionTier: (typeof MOCK_TUITION_TIERS)[number],
  parentBand: (typeof MOCK_PARENT_BANDS)[number],
  seed: number,
  outlierThreshold: number
): GroupComparisonsSchoolRow[] {
  const schoolCount = 10 + (seed % 3);
  const tuitionBandId = `mock-tb-${schoolBand.id}-${tuitionTier.name.toLowerCase().replace(/\s+/g, '-')}`;
  const tuitionBandName = tuitionTier.name;
  const rows: GroupComparisonsSchoolRow[] = [];

  const baseCost = 1900 + (seed % 6) * 350;
  const baseTuition = 2400 + (seed % 5) * 450;
  const baseSalary = 1150 + (seed % 4) * 180;

  const peerMedianCostUsd = round2(baseCost);
  const peerMedianTuitionUsd = round2(baseTuition);
  const peerMedianSalaryUsd = round2(baseSalary);
  const costSpread = peerSpread(peerMedianCostUsd);
  const tuitionSpread = peerSpread(peerMedianTuitionUsd);
  const salarySpread = peerSpread(peerMedianSalaryUsd);

  for (let i = 0; i < schoolCount; i++) {
    const jitter = ((seed + i) % 11) / 10 - 0.5;
    let cost = round2(peerMedianCostUsd * (1 + jitter * 0.35));
    let tuition = round2(peerMedianTuitionUsd * (1 + jitter * 0.3));
    let salary = round2(peerMedianSalaryUsd * (1 + jitter * 0.28));

    if (i === 0) cost = round2(peerMedianCostUsd * 1.36);
    else if (i === 1) tuition = round2(peerMedianTuitionUsd * 0.72);
    else if (i === 2) salary = round2(peerMedianSalaryUsd * 1.34);
    else if ((seed + i) % 5 === 0) cost = round2(peerMedianCostUsd * 1.28);
    else if ((seed + i) % 7 === 0) tuition = round2(peerMedianTuitionUsd * 1.3);

    const vCost = variancePct(cost, peerMedianCostUsd);
    const vTuition = variancePct(tuition, peerMedianTuitionUsd);
    const vSalary = variancePct(salary, peerMedianSalaryUsd);

    const outlierReasons = buildOutlierReasons(vCost, vTuition, vSalary, outlierThreshold);

    const governorateId = MOCK_GOVERNORATE_IDS[i % MOCK_GOVERNORATE_IDS.length]!;
    const governorateName = MOCK_GOVERNORATE_NAMES[governorateId] ?? String(governorateId);
    const nameIdx = (seed + i) % SCHOOL_NAME_PARTS.length;

    rows.push({
      schoolId: `gc-${seed}-${i}`,
      schoolCode: String(2000 + seed * 20 + i).padStart(4, '0'),
      schoolName: `${SCHOOL_NAME_PARTS[nameIdx]!} (${governorateName})`,
      governorateId,
      governorateName,
      studentsCount: 300 + (seed + i) * 55 + (i % 3) * 90,
      costPerStudentUsd: cost,
      tuitionPerStudentUsd: tuition,
      avgTeacherSalaryUsd: salary,
      peerMedianCostUsd,
      peerMedianTuitionUsd,
      peerMedianSalaryUsd,
      peerMinCostUsd: costSpread.min,
      peerMaxCostUsd: costSpread.max,
      peerMinTuitionUsd: tuitionSpread.min,
      peerMaxTuitionUsd: tuitionSpread.max,
      peerMinSalaryUsd: salarySpread.min,
      peerMaxSalaryUsd: salarySpread.max,
      varianceCostPct: vCost,
      varianceTuitionPct: vTuition,
      varianceSalaryPct: vSalary,
      budgetSubmitted: (seed + i) % 5 !== 0,
      isOutlier: outlierReasons.length > 0,
      outlierReasons,
      schoolBandId: schoolBand.id,
      schoolBandName: schoolBand.name,
      tuitionBandId,
      tuitionBandName,
      parentBandId: parentBand.id,
      parentBandName: parentBand.name,
    });
  }
  return rows;
}

/** Catalogue of mock schools — cohort per school-band × tuition tier × parent band. */
const ALL_SCHOOL_ROWS: GroupComparisonsSchoolRow[] = (() => {
  const outlierThreshold = defaultGroupComparisonsOutlierThreshold();
  const rows: GroupComparisonsSchoolRow[] = [];
  let seed = 1;
  for (const schoolBand of MOCK_SCHOOL_BANDS) {
    for (const tuitionTier of MOCK_TUITION_TIERS) {
      for (const parentBand of MOCK_PARENT_BANDS) {
        rows.push(...generateSchoolsForCohort(schoolBand, tuitionTier, parentBand, seed, outlierThreshold));
        seed++;
      }
    }
  }
  return rows;
})();

/** Total schools in mock catalogue (for dev/docs). */
export const MOCK_CATALOGUE_SCHOOL_COUNT = ALL_SCHOOL_ROWS.length;

export function focusVariance(
  row: GroupComparisonsSchoolRow,
  focus: GroupComparisonsMetricFocus
): number | null {
  if (focus === 'cost') return row.varianceCostPct;
  if (focus === 'tuition') return row.varianceTuitionPct;
  return row.varianceSalaryPct;
}

export function focusMetricValue(row: GroupComparisonsSchoolRow, focus: GroupComparisonsMetricFocus): number {
  if (focus === 'cost') return row.costPerStudentUsd;
  if (focus === 'tuition') return row.tuitionPerStudentUsd;
  return row.avgTeacherSalaryUsd;
}

export function focusPeerMedian(summary: GroupComparisonsSummary, focus: GroupComparisonsMetricFocus): number {
  if (focus === 'cost') return summary.peerMedianCostUsd;
  if (focus === 'tuition') return summary.peerMedianTuitionUsd;
  return summary.peerMedianSalaryUsd;
}

export function isOutlierOnFocus(
  row: GroupComparisonsSchoolRow,
  focus: GroupComparisonsMetricFocus,
  outlierThreshold: number
): boolean {
  const v = focusVariance(row, focus);
  return v != null && Math.abs(v) >= outlierThreshold;
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0]!;
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo]!;
  return round2(sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (idx - lo));
}

export function computeCohortDistribution(
  rows: GroupComparisonsSchoolRow[],
  metricFocus: GroupComparisonsMetricFocus
): GroupComparisonsCohortDistribution {
  const values = rows.map((r) => focusMetricValue(r, metricFocus)).filter((v) => Number.isFinite(v));
  if (values.length === 0) {
    return { peerMedian: 0, p25: 0, p75: 0, min: 0, max: 0 };
  }
  const sorted = [...values].sort((a, b) => a - b);
  return {
    peerMedian: percentile(sorted, 0.5),
    p25: percentile(sorted, 0.25),
    p75: percentile(sorted, 0.75),
    min: sorted[0]!,
    max: sorted[sorted.length - 1]!,
  };
}

function computeSummary(
  rows: GroupComparisonsSchoolRow[],
  metricFocus: GroupComparisonsMetricFocus,
  outlierThreshold: number
): GroupComparisonsSummary {
  if (rows.length === 0) {
    return {
      schoolCount: 0,
      outlierCount: 0,
      peerMedianCostUsd: 0,
      peerMedianTuitionUsd: 0,
      peerMedianSalaryUsd: 0,
    };
  }
  const costs = rows.map((r) => r.costPerStudentUsd).sort((a, b) => a - b);
  const tuitions = rows.map((r) => r.tuitionPerStudentUsd).sort((a, b) => a - b);
  const salaries = rows.map((r) => r.avgTeacherSalaryUsd).sort((a, b) => a - b);
  return {
    schoolCount: rows.length,
    outlierCount: rows.filter((r) => isOutlierOnFocus(r, metricFocus, outlierThreshold)).length,
    peerMedianCostUsd: percentile(costs, 0.5),
    peerMedianTuitionUsd: percentile(tuitions, 0.5),
    peerMedianSalaryUsd: percentile(salaries, 0.5),
  };
}

/**
 * Client-side filter over mock catalogue.
 * TODO: replace with GET /api/app/group-comparisons?Year=&GovernorateId=&SchoolBandId=&…
 */
export function filterGroupComparisons(
  params: GroupComparisonsFilterParams,
  nameHints?: { schoolBandName?: string; tuitionBandName?: string; parentBandName?: string },
  outlierThreshold: number = defaultGroupComparisonsOutlierThreshold()
): GroupComparisonsFilterResult {
  const composedPeerGroupLabel = buildComposedPeerGroupLabel({
    governorateName: params.governorateName,
    schoolBandDisplay: params.schoolBandDisplay,
    tuitionBandDisplay: params.tuitionBandDisplay,
    parentBandDisplay: params.parentBandDisplay,
  });
  const rows = ALL_SCHOOL_ROWS.filter((r) => matchesRow(r, params, nameHints)).map((row) => {
    const outlierReasons = buildOutlierReasons(
      row.varianceCostPct,
      row.varianceTuitionPct,
      row.varianceSalaryPct,
      outlierThreshold
    );
    return {
      ...row,
      isOutlier: outlierReasons.length > 0,
      outlierReasons,
    };
  });
  const summary = computeSummary(rows, params.metricFocus, outlierThreshold);
  const distribution = computeCohortDistribution(rows, params.metricFocus);
  return { composedPeerGroupLabel, summary, distribution, rows };
}

export const METRIC_FOCUS_OPTIONS: { value: GroupComparisonsMetricFocus; label: string }[] = [
  { value: 'cost', label: 'Cost per student' },
  { value: 'tuition', label: 'Tuition per student' },
  { value: 'salary', label: 'Avg teacher salary' },
];

export function metricColumnHeader(focus: GroupComparisonsMetricFocus): string {
  if (focus === 'cost') return 'Cost/student (USD)';
  if (focus === 'tuition') return 'Tuition/student (USD)';
  return 'Avg teacher salary (USD)';
}

export function peerMedianKpiLabel(focus: GroupComparisonsMetricFocus): string {
  if (focus === 'cost') return 'Peer median cost';
  if (focus === 'tuition') return 'Peer median tuition';
  return 'Peer median salary';
}
